// StatusFolgefelder.tsx — Felder, die eine Rolle im Status-Schritt direkt nach
// sich zieht (User 26.09.2026), INLINE statt als Popup:
//  - Beziehung zu Privatkontakten → Person mit Haken „lebt im selben Haushalt"
//  - Verbindung zu Unternehmen / Verein → Unternehmen bzw. Verein + Verantwortlichkeit
// Die Einträge liegen in denselben Feldern wie die Karten weiter unten
// (rel_household / rel_weitere / rel_personFirma) und stehen dort noch einmal
// als Liste.
//
// Seit 02.10.2026 im selben Gegenüber-Kasten wie die Rollen mit `bezug`
// (`forms/widgets/GegenueberKasten.tsx`): Frage oben, Suchfeld SOFORT
// darunter, weitere über „+ weiteres …". Was gespeichert wird, ist
// unverändert — dieselben Einträge in denselben Feldern.

import * as React from "react";
import { GegenueberKarte, GegenueberListe } from "../forms/widgets/GegenueberKasten";
import { HaushaltPersonFelder } from "./HouseholdList";
import { PersonFirmaFelder } from "./PersonFirmaList";
import { RELATIONSHIP_CATALOG } from "../../utils/householdRole";
import { REFINED } from "../risikoanalyseStep/tokens";
import { folgeFrage } from "../../data/gegenueberFolgen";
import { uid } from "./types";
import type { HouseholdItem, PersonFirmaItem } from "./types";

/**
 * Personen mit Beziehung, im Haushalt UND außerhalb in einer Liste. Der Haken
 * verschiebt den Eintrag zwischen `rel_household` und `rel_weitere`.
 */
export function PersonenMitBeziehungInline({
  haushalt,
  weitere,
  onHaushalt,
  onWeitere,
  startImHaushalt,
  rollen = [],
}: {
  haushalt: HouseholdItem[];
  weitere: HouseholdItem[];
  onHaushalt: (next: HouseholdItem[]) => void;
  onWeitere: (next: HouseholdItem[]) => void;
  /** Vorgabe des Hakens für neue Einträge (aus der gewählten Rolle). */
  startImHaushalt: boolean;
  /** Die gewählten Rollen — für die Frage über dem Kasten. */
  rollen?: string[];
}) {
  const alle: HouseholdItem[] = [
    ...haushalt.map((it) => ({ ...it, sameHousehold: true })),
    ...weitere.map((it) => ({ ...it, sameHousehold: false })),
  ];
  const verteilen = (next: HouseholdItem[]) => {
    onHaushalt(next.filter((it) => it.sameHousehold));
    onWeitere(next.filter((it) => !it.sameHousehold));
  };
  return (
    <GegenueberKarte frage={folgeFrage("person", rollen)}>
      <GegenueberListe<HouseholdItem>
        items={alle}
        onChange={verteilen}
        neu={() => ({
          id: uid(),
          ref: null,
          relationLabel: RELATIONSHIP_CATALOG[0],
          sameHousehold: startImHaushalt,
          freeRole: "",
          freeRoleReverse: "",
        })}
        weiteresLabel="weitere Person"
        istLeer={(it) => !it.ref}
        renderFelder={(it, update) => (
          <>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: REFINED.fontUI, fontSize: "0.9rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={it.sameHousehold}
                onChange={(e) => update({ sameHousehold: e.target.checked })}
                style={{ accentColor: REFINED.aub }}
              />
              lebt im selben Haushalt
            </label>
            <HaushaltPersonFelder it={it} update={update} sameHousehold={it.sameHousehold} />
          </>
        )}
      />
    </GegenueberKarte>
  );
}

/** Unternehmen bzw. Verein mit Verantwortlichkeit — dieselben Felder wie die Karte. */
export function UnternehmenInline({
  value,
  onChange,
  verein,
  rollen = [],
}: {
  value: PersonFirmaItem[];
  onChange: (next: PersonFirmaItem[]) => void;
  /** Nur Beschriftung: bei den Vereinsrollen heißt es „Verein". */
  verein: boolean;
  /** Die gewählten Rollen — für die Frage über dem Kasten. */
  rollen?: string[];
}) {
  return (
    <GegenueberKarte frage={folgeFrage("verbindung", rollen)}>
      <GegenueberListe<PersonFirmaItem>
        items={value}
        onChange={onChange}
        neu={() => ({ id: uid(), ref: null, verant: null })}
        weiteresLabel={verein ? "weiterer Verein" : "weiteres Unternehmen"}
        istLeer={(it) => !it.ref}
        renderFelder={(it, update) => <PersonFirmaFelder it={it} update={update} verein={verein} />}
      />
    </GegenueberKarte>
  );
}
